import { useCallback, useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { getSchedule } from '../api/client'
import { ApiError, ScheduleResponse } from '../api/types'
import { config } from '../config'

export type ScheduleState =
  | { state: 'loading' }
  | { state: 'error'; error: ApiError }
  | { state: 'ready'; data: ScheduleResponse; lastFetchedAt: Date }

/**
 * Loads GET /api/v1/me/schedule and keeps it fresh. Polls every 60s, or
 * every 15s while a window is pending or cancelling, so the customer sees it
 * confirmed without pressing refresh.
 */
export function useSchedule(systemId?: number) {
  const { instance } = useMsal()
  const [result, setResult] = useState<ScheduleState>({ state: 'loading' })
  const inflight = useRef(false)

  const fetchData = useCallback(async () => {
    if (inflight.current) return
    inflight.current = true
    try {
      const data = await getSchedule(instance, systemId)
      setResult({ state: 'ready', data, lastFetchedAt: new Date() })
    } catch (err) {
      setResult((prev) =>
        // Keep showing the last good data through a transient error.
        prev.state === 'ready' && (err as ApiError).status >= 500 ? prev : { state: 'error', error: err as ApiError })
    } finally {
      inflight.current = false
    }
  }, [instance, systemId])

  const waiting =
    result.state === 'ready' &&
    result.data.windows.some((w) => w.status === 'PENDING' || w.status === 'CANCEL_PENDING')
  const interval = waiting ? config.scheduleFastRefreshMs : config.refreshIntervalMs

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const timer = setInterval(fetchData, interval)
    return () => clearInterval(timer)
  }, [fetchData, interval])

  return { result, refresh: fetchData }
}
